import type { BuilderSelection } from "@keydials/dial-workspace";

/**
 * The builder's five stages (KEY-560), in order. Only these rules live here, so the frame, the stage rail and the address bar
 * can't disagree about where an author may go.
 */
export type BuilderStageId = "start" | "build" | "colours" | "details" | "launch";

export const BUILDER_STAGES: readonly BuilderStageId[] = ["start", "build", "colours", "details", "launch"];

/** Stages an author may pass without doing anything. Details is the only one: a first dial is recommended to skip it. */
export const SKIPPABLE_STAGES: ReadonlySet<BuilderStageId> = new Set<BuilderStageId>(["details"]);

/** What the builder knows about the design it has open, enough to place the author. */
export interface StageFacts {
  /** False until Start has created the draft. Creating costs a design slot, so nothing past Start opens before it. */
  created: boolean;
  hasSections: boolean;
  lifecycle: "Draft" | "Checking" | "Published" | null;
}

/** The stages the rail lets the author open. */
export function enterableStages(facts: StageFacts): BuilderStageId[] {
  return facts.created ? [...BUILDER_STAGES] : ["start"];
}

/** Where a design opens when the author comes back to it. A test copy or a running dial opens at Launch. */
export function openingStage(facts: StageFacts): BuilderStageId {
  if (!facts.created) return "start";
  if (facts.lifecycle === "Checking" || facts.lifecycle === "Published") return "launch";
  return facts.hasSections ? "build" : "start";
}

export function nextStage(stage: BuilderStageId): BuilderStageId | null {
  const index = BUILDER_STAGES.indexOf(stage);
  return index >= 0 && index < BUILDER_STAGES.length - 1 ? BUILDER_STAGES[index + 1] : null;
}

export function previousStage(stage: BuilderStageId): BuilderStageId | null {
  const index = BUILDER_STAGES.indexOf(stage);
  return index > 0 ? BUILDER_STAGES[index - 1] : null;
}

/** Reads `?stage=` from the address bar. Anything unrecognised is ignored rather than trusted. */
export function parseStage(value: string | null | undefined): BuilderStageId | null {
  return BUILDER_STAGES.find((stage) => stage === value) ?? null;
}

/** The stage to show: the one asked for when the author may open it, otherwise where the design opens. */
export function resolveStage(requested: BuilderStageId | null, facts: StageFacts): BuilderStageId {
  return requested && enterableStages(facts).includes(requested) ? requested : openingStage(facts);
}

/** How a new dial begins when a way in has already chosen (`?start=`): Describe it, a template, or blank. */
export type StartChoice = "describe" | "template" | "blank";

/** Reads `?start=` from the address bar. Anything unrecognised is ignored, and Start asks as usual. */
export function parseStart(value: string | null | undefined): StartChoice | null {
  return value === "describe" || value === "template" || value === "blank" ? value : null;
}

/** Where "Fix this" takes the author: a field is fixed on Details, and a section, an action point or an item on Build. */
export function fixStage(selection: BuilderSelection): "build" | "details" {
  return selection.level === "field" ? "details" : "build";
}
